import { format, isValid } from "date-fns";

export const CODIGO_EXITO = "0000";
export const MENSAJE_ERROR_CONEXION = "No fue posible conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.";
export const MENSAJE_ERROR_GENERICO = "No fue posible registrar la medición.";
export const MENSAJE_EXITO_GENERICO = "Medición registrada.";

/**
 * Interpreta la respuesta de un endpoint de creación de mediciones.
 * El éxito se decide solo por codigoRespuesta === "0000" (el backend
 * responde HTTP 200 también en error). Nunca lanza excepción.
 * @param {Object|undefined|null} response lo que devuelve request()
 * @returns {{ok: boolean, mensaje: string}}
 */
export function interpretarRespuestaCreacion(response) {
	if (response == null) {
		return { ok: false, mensaje: MENSAJE_ERROR_CONEXION };
	}
	const ok = response.codigoRespuesta === CODIGO_EXITO;
	const mensaje = typeof response.mensaje === "string" && response.mensaje.trim() !== ""
		? response.mensaje
		: (ok ? MENSAJE_EXITO_GENERICO : MENSAJE_ERROR_GENERICO);
	return { ok, mensaje };
}

export const SIN_CLASIFICACION = "Sin clasificación";

/**
 * Texto de la clasificación devuelta por el backend en el registro de perímetro
 * abdominal. Nunca lanza excepción.
 * @param {Object|null|undefined} respuesta campo `respuesta` del envoltorio
 * @returns {string}
 */
export function textoClasificacionPerimetro(respuesta) {
	const clasificacion = respuesta ? respuesta.clasificacion : null;
	return typeof clasificacion === "string" && clasificacion.trim() !== ""
		? clasificacion
		: SIN_CLASIFICACION;
}

// Rangos sugeridos de perímetro abdominal (cm), alineados con la semilla
// evbi.rangos 'perimetro_abdominal' del backend (tramos min <= valor < max).
// Solo informativos: la clasificación oficial la devuelve el backend.
export const RANGOS_SUGERIDOS_PERIMETRO = Object.freeze([
	Object.freeze({ categoria: "Normal", hombres: "menos de 94 cm", mujeres: "menos de 80 cm" }),
	Object.freeze({ categoria: "Riesgo aumentado", hombres: "de 94 a menos de 102 cm", mujeres: "de 80 a menos de 88 cm" }),
	Object.freeze({ categoria: "Riesgo muy aumentado", hombres: "102 cm o más", mujeres: "88 cm o más" }),
]);

// Rangos sugeridos de glucometría (mg/dL), alineados con la semilla evbi.rangos
// 'glucometria' del backend. Solo informativos: la clasificación la devuelve el backend.
export const RANGOS_SUGERIDOS_GLUCOMETRIA = Object.freeze([
	Object.freeze({ categoria: "Saludable", rango: "0-99 mg/dL" }),
	Object.freeze({ categoria: "Normal", rango: "100-125 mg/dL" }),
	Object.freeze({ categoria: "Alto", rango: "126 mg/dL o más" }),
]);
export const NOTA_RANGOS_GLUCOMETRIA = "La clasificación del registro la asigna el sistema al guardar.";

// Rangos sugeridos de espirometría (porcentaje PEF/PRED), alineados con la semilla
// evbi.rangos 'espirometria' del backend. Solo informativos.
export const RANGOS_SUGERIDOS_ESPIROMETRIA = Object.freeze([
	Object.freeze({ categoria: "Normal", rango: "80-100 %" }),
	Object.freeze({ categoria: "Anormal", rango: "menos de 80 %" }),
]);
export const NOTA_RANGOS_ESPIROMETRIA =
	"Rangos sobre el porcentaje PEF/PRED. La clasificación del registro la asigna el sistema al guardar.";

// ---- Historial de mediciones (tabla paginada en servidor) ----

/** Relleno de celda cuando el dato no es interpretable. */
export const SIN_DATO = "—";
export const HISTORIAL_TAMANO_INICIAL = 10;
export const HISTORIAL_OPCIONES_TAMANO = Object.freeze([5, 10, 25]);
export const TITULO_HISTORIAL = "Historial";
export const MENSAJE_HISTORIAL_VACIO = "No hay mediciones registradas";
export const MENSAJE_HISTORIAL_ERROR = "No se pudo cargar el historial";

/**
 * Fecha del historial en hora local, formato dd/MM/yyyy HH:mm. Acepta cadena
 * ISO-8601 o milisegundos. Nunca lanza excepción.
 * @param {string|number|null|undefined} created
 * @returns {string}
 */
export function formatearFechaHistorial(created) {
	if (typeof created !== "string" && typeof created !== "number") {
		return SIN_DATO;
	}
	const fecha = new Date(created);
	return isValid(fecha) ? format(fecha, "dd/MM/yyyy HH:mm") : SIN_DATO;
}

/**
 * Valor numérico seguido de su unidad (p. ej. "95 mg/dL"). Nunca lanza excepción.
 * @param {number} valor
 * @param {string} unidad
 * @returns {string}
 */
export function formatearValorConUnidad(valor, unidad) {
	return typeof valor === "number" && Number.isFinite(valor) ? `${valor} ${unidad}` : SIN_DATO;
}

/**
 * Clasificación (rangoValor) del historial; vacío o ausente => "Sin clasificación".
 * @param {string|null|undefined} rangoValor
 * @returns {string}
 */
export function textoClasificacionHistorial(rangoValor) {
	return typeof rangoValor === "string" && rangoValor.trim() !== ""
		? rangoValor
		: SIN_CLASIFICACION;
}

/**
 * Interpreta la respuesta de un endpoint de historial paginado.
 * Nunca lanza excepción.
 * @param {Object|undefined|null} response lo que devuelve request()
 * @returns {{ok: false}|{ok: true, filas: Array, totalElements: number}}
 */
export function interpretarRespuestaHistorial(response) {
	if (response == null || response.error === true || !Array.isArray(response.content)) {
		return { ok: false };
	}
	const total = Number(response.totalElements);
	return {
		ok: true,
		filas: response.content,
		totalElements: Number.isFinite(total) ? total : response.content.length,
	};
}

// Columnas del historial: { id, label, valor(fila) => string }
export const COLUMNAS_HISTORIAL_GLUCOMETRIA = Object.freeze([
	{ id: "fecha", label: "Fecha", valor: (f) => formatearFechaHistorial(f.created) },
	{ id: "glucometria", label: "Glucometría", valor: (f) => formatearValorConUnidad(f.glucometria, "mg/dL") },
	{ id: "clasificacion", label: "Clasificación", valor: (f) => textoClasificacionHistorial(f.rangoValor) },
]);

export const COLUMNAS_HISTORIAL_PERIMETRO = Object.freeze([
	{ id: "fecha", label: "Fecha", valor: (f) => formatearFechaHistorial(f.created) },
	{ id: "perimetro", label: "Perímetro", valor: (f) => formatearValorConUnidad(f.perimetro, "cm") },
	{ id: "clasificacion", label: "Clasificación", valor: (f) => textoClasificacionHistorial(f.clasificacion) },
]);

// Espirometría: la fecha viene en datecreated (no en created)
export const COLUMNAS_HISTORIAL_ESPIROMETRIA = Object.freeze([
	{ id: "fecha", label: "Fecha", valor: (f) => formatearFechaHistorial(f.datecreated) },
	{ id: "pef", label: "PEF", valor: (f) => formatearValorConUnidad(f.pef, "L/min") },
	{ id: "pred", label: "PRED", valor: (f) => formatearValorConUnidad(f.pred, "L/min") },
	{ id: "porcentaje", label: "Porcentaje", valor: (f) => formatearValorConUnidad(f.porcentaje, "%") },
	{ id: "clasificacion", label: "Clasificación", valor: (f) => textoClasificacionHistorial(f.rangoValor) },
]);

// Lipidograma: cada lípido muestra su valor y, debajo (detalle), su clasificación propia
export const COLUMNAS_HISTORIAL_LIPIDOGRAMA = Object.freeze([
	{ id: "fecha", label: "Fecha", valor: (f) => formatearFechaHistorial(f.created) },
	{
		id: "colesterol",
		label: "Colesterol",
		valor: (f) => formatearValorConUnidad(f.colesterol, "mg/dL"),
		detalle: (f) => textoClasificacionHistorial(f.rangoColesterol),
	},
	{
		id: "ldl",
		label: "LDL",
		valor: (f) => formatearValorConUnidad(f.ldl, "mg/dL"),
		detalle: (f) => textoClasificacionHistorial(f.rangoLdl),
	},
	{
		id: "hdl",
		label: "HDL",
		valor: (f) => formatearValorConUnidad(f.hdl, "mg/dL"),
		detalle: (f) => textoClasificacionHistorial(f.rangoHdl),
	},
	{
		id: "trigliceridos",
		label: "Triglicéridos",
		valor: (f) => formatearValorConUnidad(f.trigliceridos, "mg/dL"),
		detalle: (f) => textoClasificacionHistorial(f.rangoTrigliceridos),
	},
]);
